import { addRendition } from '@shared/utils/photoRendition';
import { getConfig } from '@config';
import { isUrlWithHttpProtocol } from '@shared/utils/urlHelpers';

export type Rendition = 'small'; // A tested subset of media service rendition choices. Medium is default and not included.

const isDev = getConfig().environment === 'dev';
const MEDIA_SERVICE = isDev ? 'https://dev.withjoy.com/media' : 'https://withjoy.com/media';

/**
 * Minimum layout dimension (screen px) below which we skip rendition resizing.
 *
 * Why 50px: media_service scales the shorter image dimension up to the rendition
 * target (960px for medium). Template images are ~4× layout size, so a 50px layout
 * → 200px actual → 4.8× upscale. At that ratio the longer dimension stays safely
 * under the WebP/libvips 16,383px ceiling even on the widest cards (~853px layout).
 *
 * Lower thresholds (10–20px) still allow 12–24× upscales that easily exceed the
 * WebP limit for standard card widths — causing 500s from media_service.
 *
 * This guards against decorative assets like thin borders and dividers (e.g. 4×76px
 * or 792×4px strips exported from Figma) whose extreme aspect ratios produce absurd
 * upscaled dimensions (960×18,240 or 190,080×960).
 */
const MIN_LAYOUT_DIM_FOR_RENDITION = 50;

const MEDIA_SERVICE_BASES = ['https://withjoy.com/media', 'https://dev.withjoy.com/media'];

export function isMediaServiceUrl(url: string) {
  return MEDIA_SERVICE_BASES.some(base => url.startsWith(base));
}

/**
 * Rewrite a media_service URL to its `raw/` variant — `.../media/<path>` becomes
 * `.../media/raw/<path>` — which serves the exact bytes that were uploaded.
 *
 * The default (non-raw) endpoint transcodes on read: a WebP file uploaded as
 * `.jpeg` is served back as genuine JPEG bytes. Anything inspecting the stored
 * format must therefore read the raw URL, or it only ever sees media_service's
 * re-encoded output.
 *
 * Returns the URL unchanged when it is not a media_service URL or is already raw.
 */
export function rawMediaServiceUrl(url: string): string {
  for (const base of MEDIA_SERVICE_BASES) {
    if (!url.startsWith(`${base}/`)) continue;
    const path = url.slice(base.length + 1);
    return path.startsWith('raw/') ? url : `${base}/raw/${path}`;
  }
  return url;
}

export const applyRenditionIfMediaServiceUrl = (url: string, rendition?: 'small', layoutDims?: Readonly<{ width: number; height: number }>) => {
  if (!isMediaServiceUrl(url)) return url;
  if (layoutDims && Math.min(layoutDims.width, layoutDims.height) < MIN_LAYOUT_DIM_FOR_RENDITION) {
    return url;
  }
  return addRendition({ url, renditionSize: rendition ?? 'medium' });
};

export function absolutePhotoUrl(imageLayerSrc: string) {
  if (isUrlWithHttpProtocol(imageLayerSrc) || imageLayerSrc.startsWith('var(')) {
    return imageLayerSrc;
  }
  return imageLayerSrc.includes('/') ? `${MEDIA_SERVICE}/${imageLayerSrc}` : `https://withjoy.com/media/paper/${imageLayerSrc}`;
}
